import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtNum, fmtMoney, fmtPct, isoDaysAgo, today } from "@/lib/format";
import KpiCard from "@/components/dashboard/KpiCard";
import DateRangePicker from "@/components/dashboard/DateRangePicker";
import {
  SpendClicksChart, FrequencyCpmChart, CountryReachChart, CtrByCampaignChart,
} from "@/components/dashboard/PerformanceCharts";
import { Progress } from "@/components/ui/progress";
import { Loader2, Database, Radio } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const [range, setRange] = useState({ start: isoDaysAgo(13), end: today(), preset: 14 });
  const [compare, setCompare] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard", {
        params: { start: range.start, end: range.end, compare },
      });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end, compare]);

  useEffect(() => { load(); }, [load]);

  const k = data?.kpis;
  const d = data?.deltas || {};

  return (
    <div className="space-y-5 sm:space-y-6 animate-fade-up">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {(data?.data_sources || []).map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full bg-[#14181B] border border-[#242B30] text-zinc-400">
                <Database className="h-3 w-3" /> {s} data
              </span>
            ))}
          </div>
        </div>
        <DateRangePicker range={range} setRange={setRange} compare={compare} setCompare={setCompare} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-6 w-6 text-lime-400 animate-spin" />
        </div>
      ) : !k || k.impressions === 0 ? (
        <div data-testid="dashboard-empty" className="text-center py-24 bg-[#14181B] border border-[#242B30] rounded-xl">
          <Radio className="h-10 w-10 text-lime-400 mx-auto mb-4" />
          <p className="text-white font-display text-lg">No live performance data yet</p>
          <p className="text-zinc-500 text-sm mt-2 font-mono max-w-md mx-auto">
            Connect your Meta ad account to pull real campaigns, spend and conversions — or import a Meta Ads CSV in Reports.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Link to="/meta-integration" data-testid="empty-connect-meta"
              className="inline-flex items-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-5 py-2.5 text-sm font-semibold text-[#0A0D0E]">
              <Radio className="h-4 w-4" /> Connect Meta account
            </Link>
            <Link to="/reports" className="inline-flex items-center gap-2 rounded-full border border-[#242B30] text-zinc-300 hover:text-white px-5 py-2.5 text-sm">
              <Database className="h-4 w-4" /> Import CSV
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Budget bar */}
          <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-zinc-500">Budget Utilization</span>
              <span className="font-mono text-xs sm:text-sm text-white">
                {fmtMoney(k.spend, cur)} / {fmtMoney(k.budget_total, cur)}
              </span>
            </div>
            <Progress value={Math.min(k.budget_utilization, 100)} className="h-2 bg-[#242B30]" />
            <div className="mt-2 font-mono text-xs text-lime-400">{fmtPct(k.budget_utilization)} of active daily budget</div>
          </div>

          {/* KPI grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <KpiCard testId="kpi-spend" title="Ad Spend" value={fmtMoney(k.spend, cur)} delta={d.spend} />
            <KpiCard testId="kpi-reach" title="Reach" value={fmtNum(k.reach)} delta={d.reach} />
            <KpiCard testId="kpi-impressions" title="Impressions" value={fmtNum(k.impressions)} delta={d.impressions} />
            <KpiCard testId="kpi-frequency" title="Frequency" value={k.frequency.toFixed(2)} delta={d.frequency} invert hint="impressions / reach" />
            <KpiCard testId="kpi-cpm" title="CPM" value={fmtMoney(k.cpm, cur)} delta={d.cpm} invert hint="cost / 1k impr." />
            <KpiCard testId="kpi-clicks" title="Outbound Clicks" value={fmtNum(k.outbound_clicks)} delta={d.outbound_clicks} />
            <KpiCard testId="kpi-ctr" title="CTR" value={fmtPct(k.ctr)} delta={d.ctr} hint="clicks / impr." />
            <KpiCard testId="kpi-cpc" title="CPC" value={fmtMoney(k.cpc, cur)} delta={d.cpc} invert hint="cost / click" />
            <KpiCard testId="kpi-lpv" title="Landing Page Views" value={fmtNum(k.landing_page_views)} delta={d.landing_page_views} />
            <KpiCard testId="kpi-cost-lpv" title="Cost / LP View" value={fmtMoney(k.cost_per_lpv, cur)} delta={d.cost_per_lpv} invert />
            <KpiCard testId="kpi-conversions" title="Conversions" value={fmtNum(k.conversions)} delta={d.conversions} />
            <KpiCard testId="kpi-cost-conv" title="Cost / Conversion" value={fmtMoney(k.cost_per_conversion, cur)} delta={d.cost_per_conversion} invert />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <SpendClicksChart data={data.by_date} />
            <FrequencyCpmChart data={data.by_date} />
            <CountryReachChart data={data.by_country} />
            <CtrByCampaignChart data={data.by_campaign} />
          </div>
        </>
      )}
    </div>
  );
}
